import { OrganizationFilterType } from "@/components/search/OrganizationFilter.vue";
import { Customer } from "@/types/Customer";
import { Label } from "@/types/Label";
import { LightBoxArtifact } from "@/types/media/LightBoxArtifact";
import { DateFilterType } from "@/types/search/DateFilterType";
import { FilterType } from "@/types/search/FilterType";
import { IndexableDocument } from "@/types/search/IndexableDocument";
import { IndexableEntityType } from "@/types/search/IndexableEntityTypes";
import { SearchRequest, searchRequestToQueryString } from "@/types/search/SearchRequest";
import { User } from "@/types/User";
import { logWarn } from "@/helpers/logError";
import type { Router } from "vue-router";

export const specialCharactersString = /[^A-Za-z0-9_ ]/g;
export const specialCharacters = new RegExp(specialCharactersString);

// "organization" isn't purely a user-selected filter value like the others below -
// parseSearchRequest() unconditionally defaults it to "current" as a structural sentinel
// (it's what keeps getSearchRequest() non-null for an otherwise-empty filter set, and
// distinguishes current-org vs all-orgs scope in loadPage()'s search/non-search branch).
// Deleting it outright when hidden silently reverts that default too, not just a race-window
// override - reset it to "current" instead so the field always exists once hidden (LSP-17781
// bugbot finding: contact-scoped ProjectsPage's first load was taking the wrong endpoint).
const hiddenFilterResets: Record<Exclude<FilterType, "organization">, readonly (keyof SearchRequest)[]> = {
	contentType: ["contentTypes"],
	status: ["statuses"],
	customer: ["customerIds"],
	user: ["userIds", "createdByIds"],
	tag: ["labels"],
	date: ["startDate", "endDate", "dateFilter"],
	search: ["searchTerm"]
};

// LSP-17781: strip a filter's value from searchRequest once it becomes hidden (e.g. after
// hiddenFilters resolves post-init), since Filters.vue hiding the control leaves no UI to clear it.
export function clearHiddenFilterValues(hiddenFilters: FilterType[], searchRequest: Partial<SearchRequest>) {
	for (const hiddenFilter of hiddenFilters) {
		if (hiddenFilter === "organization") {
			const discardedValue = searchRequest.organization;
			if (discardedValue !== undefined && discardedValue !== "current") {
				logWarn("[filters] Resetting hidden filter value", { hiddenFilter, field: "organization", discardedValue });
			}
			searchRequest.organization = "current";
			continue;
		}
		for (const field of hiddenFilterResets[hiddenFilter]) {
			const discardedValue = searchRequest[field];
			if (discardedValue === undefined) continue;
			logWarn("[filters] Clearing hidden filter value", { hiddenFilter, field, discardedValue });
			delete searchRequest[field];
		}
	}
}

const entityIndexSortMapping: Partial<Record<IndexableEntityType, Record<string, string>>> = {
	conversation: {
		number: "number",
		name: "name",
		customerName: "customerName",
		projectName: "projectName",
		agentName: "userName",
		labelName: "labelsFlat",
		status: "status",
		createdAt: "created",
		userName: "agentName",
		labelsFlat: "labelName",
		created: "createdAt"
	},
	project: {
		name: "name",
		customerName: "customerName",
		agentName: "userName",
		labelnames: "labelsFlat",
		status: "status",
		userName: "agentName",
		createdBy: "agentName",
		labelsFlat: "labelnames",
		created: "createdAt"
	},
	customer: {
		firstname: "customerFirstName",
		lastname: "customerLastName",
		// LSP-18031: map the real header key so phone sorting does not silently fall back to "created".
		phones: "customerPhoneNumbers",
		phoneNumbers: "customerPhoneNumbers",
		emails: "customerEmails",
		customerFirstName: "firstname",
		customerLastName: "lastname",
		customerPhoneNumbers: "phones",
		customerEmails: "emails"
	}
};

export function sortCustomersBySelected(customers: Array<Customer>, selectedCustomers: Array<string>): Array<Customer> {
	const selectedSet = new Set(selectedCustomers);

	const selected = [];
	const remaining = [];

	for (const customer of customers) {
		if (selectedSet.has(customer.id)) {
			selected.push(customer);
		} else {
			remaining.push(customer);
		}
	}

	// Sort selected according to the selectedCustomers order
	selected.sort((a, b) => selectedCustomers.indexOf(a.id) - selectedCustomers.indexOf(b.id));
	return [...selected, ...remaining];
}

export function sortUsersBySelected(users: Array<User>, selectedUsers: Array<string>): Array<User> {
	const selectedSet = new Set(selectedUsers);

	const selected = [];
	const remaining = [];

	for (const user of users) {
		if (selectedSet.has(user.id)) {
			selected.push(user);
		} else {
			remaining.push(user);
		}
	}

	// Sort selected according to the selectedUsers order
	selected.sort((a, b) => selectedUsers.indexOf(a.id) - selectedUsers.indexOf(b.id));
	return [...selected, ...remaining];
}

export function sortContentTypesBySelected(contentTypes: Array<string>, selectedContentTypes: Array<string>): Array<string> {
	const selectedSet = new Set(selectedContentTypes);

	const selected = [];
	const remaining = [];

	for (const contentType of contentTypes) {
		if (selectedSet.has(contentType)) {
			selected.push(contentType);
		} else {
			remaining.push(contentType);
		}
	}

	selected.sort((a, b) => selectedContentTypes.indexOf(a) - selectedContentTypes.indexOf(b));
	return [...selected, ...remaining];
}

export function sortTagsBySelected(tags: Array<Label>, selectedTags: Array<string>): Array<Label> {
	const selectedSet = new Set(selectedTags);

	const selected = [];
	const remaining = [];

	for (const tag of tags) {
		if (selectedSet.has(tag.id)) {
			selected.push(tag);
		} else {
			remaining.push(tag);
		}
	}

	selected.sort((a, b) => selectedTags.indexOf(a.id) - selectedTags.indexOf(b.id));
	return [...selected, ...remaining];
}

export function sortStatusesBySelected(statuses: Array<string>, selectedStatuses: Array<string>): Array<string> {
	const selectedSet = new Set(selectedStatuses);

	const selected = [];
	const remaining = [];

	for (const status of statuses) {
		if (selectedSet.has(status)) {
			selected.push(status);
		} else {
			remaining.push(status);
		}
	}

	selected.sort((a, b) => selectedStatuses.indexOf(a) - selectedStatuses.indexOf(b));
	return [...selected, ...remaining];
}

export function parseSortDirection(
	query: Record<string, unknown> | undefined,
	prefixedParamName: string,
	defaultDirection: boolean
): boolean {
	let queryValue: unknown;
	try {
		const prefixedValue =
			query && Object.prototype.hasOwnProperty.call(query, prefixedParamName) ? query[prefixedParamName] : undefined;
		queryValue =
			prefixedValue ??
			(query && Object.prototype.hasOwnProperty.call(query, "orderByDescending") ? query.orderByDescending : undefined);
	} catch (error) {
		logWarn("[filters] Failed to read sort direction query value", {
			prefixedParamName,
			queryValue,
			defaultDirection,
			error
		});
		return defaultDirection;
	}

	if (queryValue === true || queryValue === "true") return true;
	if (queryValue === false || queryValue === "false") return false;
	if (queryValue !== undefined) {
		logWarn("[filters] Invalid sort direction query value", {
			prefixedParamName,
			queryValue,
			defaultDirection
		});
	}

	return defaultDirection;
}

// LSP-18407 (review follow-up): mirrors the server's own search clamp — contact-api's
// SearchRequest.MAX_PAGE_SIZE is 150, so a larger client value can only ever be trimmed there anyway.
const SEARCH_MAX_PAGE_SIZE = 150;

export function parseSearchQuery(query: Record<string, string>, contentTypes: IndexableEntityType[] = []): Partial<SearchRequest> {
	if (query) {
		const searchRequest: Partial<SearchRequest> = {};

		if (contentTypes.length > 0) {
			searchRequest.contentTypes = contentTypes;
		} else if (query.contentTypes) {
			searchRequest.contentTypes = (query.contentTypes as string).split(",") as IndexableEntityType[];
		}

		if (query.searchTerm) {
			searchRequest.searchTerm = query.searchTerm;
		}

		if (query.customerIds) {
			searchRequest.customerIds = query.customerIds.split(",");
		}

		if (query.createdByIds) {
			searchRequest.createdByIds = query.createdByIds.split(",");
		}
		if (query.userIds) {
			searchRequest.userIds = query.userIds.split(",");
		}

		if (query.statuses) {
			searchRequest.statuses = query.statuses.split(",");
		}

		if (query.startDate) {
			try {
				const dateNumber = parseInt(query.startDate);
				searchRequest.startDate = new Date(dateNumber);
			} catch (e) {}
		}

		if (query.endDate) {
			try {
				const dateNumber = parseInt(query.endDate);
				searchRequest.endDate = new Date(dateNumber);
			} catch (e) {}
		}

		if (query.dateFilter) {
			searchRequest.dateFilter = query.dateFilter as DateFilterType;
		}

		if (query.labels) {
			searchRequest.labels = query.labels.split(",");
		}

		if (query.orderBy) {
			searchRequest.orderBy = query.orderBy;
		}

		if (query.orderByDescending) {
			searchRequest.orderByDescending = query.orderByDescending === "true";
		}

		if (query.tab) {
			searchRequest.tab = query.tab;
		}

		if (query.entityType) {
			searchRequest.entityType = query.entityType as IndexableEntityType;
		}

		// LSP-18407 (review follow-up): bare parseInt let a hand-edited ?page=/?pageSize= through
		// unbounded. EntityResultsPage assigns this straight into searchRequest and awaits search() in
		// the same onMounted turn, so the first entitySearch went out with whatever the URL said —
		// the watcher that would have overwritten page/pageSize only runs on the next tick.
		if (query.page) {
			searchRequest.page = resolveQueryPage(query.page, 1);
		}

		if (query.pageSize) {
			const requestedPageSize = parseWholePositiveInteger(query.pageSize);
			// Left unset when it is not a whole positive integer, so the caller's own default applies —
			// the same thing an absent parameter does today.
			if (requestedPageSize !== undefined) {
				searchRequest.pageSize = Math.min(requestedPageSize, SEARCH_MAX_PAGE_SIZE);
			}
		}

		if (query.organization) {
			searchRequest.organization = query.organization as OrganizationFilterType;
		}

		if (query.score) {
			searchRequest.score = query.score === "true";
		}

		if (query.latitude) {
			searchRequest.latitude = parseFloat(query.latitude);
			if (isNaN(searchRequest.latitude)) {
				console.warn("Failed to parse latitude value", query.latitude);
			}
		}

		if (query.longitude) {
			searchRequest.longitude = parseFloat(query.longitude);
			if (isNaN(searchRequest.longitude)) {
				console.warn("Failed to parse longitude value", query.longitude);
			}
		}

		return searchRequest;
	} else {
		return {} as SearchRequest;
	}
}

// MK - Removed the special logic for special characters but leaving these in here in case we need to add other logic.

export function handleSearchKeyDown(event: KeyboardEvent) {
	// Handle keydown events
}

export function handleSearchInput(event: InputEvent) {
	// Handle any search input cases
}

export function handleSearchPaste(event: ClipboardEvent): boolean {
	const pastedText = event.clipboardData?.getData("text") || "";
	// Handle any cases for pasted input
	return true;
}

export function cleanSearchTerm(searchTerm: string) {
	if (!searchTerm || searchTerm.length === 0) {
		return "";
	}

	return searchTerm;
}

/**
 * Synchronously blur the underlying <input> so mobile soft keyboards (iOS
 * Safari in particular) dismiss on search submit (LSP-15898).
 *
 * iOS Safari only dismisses the keyboard when blur() runs inside the active
 * user gesture, on the real DOM <input>. Calling .blur() on a Vue component
 * proxy is unreliable across Vuetify versions, so we resolve the input via
 * `event.target` first (the @keyup.enter path, where target IS the input),
 * with a ref fallback for the @click:append-inner path where event.target
 * is the icon element instead.
 */
export function dismissSoftKeyboard(event: Event | undefined, componentRef: { $el?: HTMLElement | null } | null | undefined): void {
	const eventInput = event?.target instanceof HTMLInputElement ? event.target : null;
	const fallbackInput = (componentRef?.$el?.querySelector?.("input") as HTMLInputElement | null) ?? null;
	(eventInput ?? fallbackInput)?.blur();
}

/**
 * LSP-21228: `history: "push"` records the write as a browser history entry so Back undoes a meaningful
 * filter/sort change; the default "replace" is for search typing, paging and restoring state from the URL.
 * An unchanged query is never written, so a push can never create a duplicate entry.
 */
export type QueryHistoryOptions = { history?: "push" | "replace" };

function writeQuery(router: Router, query: Record<string, string>, options: QueryHistoryOptions) {
	// Fire-and-forget, as these syncs always were: the page has already applied the state being mirrored.
	if (options.history === "push") {
		void router.push({ query });
	} else {
		void router.replace({ query });
	}
}

export function updateSearchQuery(
	router: Router | undefined,
	searchRequest: SearchRequest,
	routeName?: string,
	preservedParams?: Record<string, string>,
	preservePagination = false,
	options: QueryHistoryOptions = {}
) {
	if (!router) {
		return;
	}

	// Skip if the route has changed since the caller initiated the update (e.g. user navigated away quickly)
	if (routeName && router.currentRoute?.value?.name !== routeName) {
		return;
	}

	const currentQuery = router.currentRoute?.value?.query ?? {};

	// Clear the query if the searchRequest is empty and there
	if (!searchRequest) {
		if (Object.keys(currentQuery).length > 0) {
			router.replace({ query: {} });
		}

		return;
	}
	// build new query from populated fields
	const params = new URLSearchParams(searchRequestToQueryString(searchRequest));
	const nextQuery: Record<string, string> = {};
	for (const [key, value] of params) {
		if ((!preservePagination && (key === "page" || key === "pageSize")) || !value.trim()) continue;
		nextQuery[key] = value;
	}

	// Preserve fromSearch and searchState params when updating query
	if (currentQuery.fromSearch) {
		nextQuery.fromSearch = currentQuery.fromSearch as string;
	}
	if (currentQuery.searchState) {
		nextQuery.searchState = currentQuery.searchState as string;
	}

	// Preserve additional params (e.g. shareId, artifactId on entity detail pages)
	if (preservedParams) {
		for (const [key, value] of Object.entries(preservedParams)) {
			if (value) {
				nextQuery[key] = value;
			}
		}
	}

	// only write if different
	if (!shallowEqual(currentQuery as Record<string, any>, nextQuery)) {
		writeQuery(router, nextQuery, options);
	}
}

export function updateQuery(
	router: Router | undefined,
	query: Record<string, string>,
	routeName?: string,
	options: QueryHistoryOptions = {}
) {
	if (!router) {
		return;
	}

	// Skip if the route has changed since the caller initiated the update (e.g. user navigated away quickly)
	if (routeName && router.currentRoute?.value?.name !== routeName) {
		return;
	}

	const currentQuery = router.currentRoute?.value?.query ?? {};

	// Preserve fromSearch and searchState params when updating query
	const preservedParams: Record<string, string> = {};
	if (!query.fromSearch && currentQuery.fromSearch) {
		preservedParams.fromSearch = currentQuery.fromSearch as string;
	}
	if (!query.searchState && currentQuery.searchState) {
		preservedParams.searchState = currentQuery.searchState as string;
	}

	const mergedQuery = { ...query, ...preservedParams };
	if (!shallowEqual(currentQuery as Record<string, any>, mergedQuery)) {
		writeQuery(router, mergedQuery, options);
	}
}

export function getSortParam(sortBy: string, entityType: IndexableEntityType, fallback: boolean = true) {
	// break-it round 1
	// The own-key rule has to cover BOTH index reads. `sortBy` arrives straight off the URL query, so a plain
	// index read returns inherited Object.prototype members — `?orderBy=constructor` handed back a *function*
	// that CustomersPage then used as a sort column, and buildSortOrder calls .indexOf on it. `entityType` is
	// typed and every call site passes a string literal, but an unguarded `entityIndexSortMapping["__proto__"]`
	// resolves to `Object.prototype`, whose own keys include `constructor` — so guarding only the second read
	// would wave through the very function value this guard exists to block.
	// `Object.prototype.hasOwnProperty.call` rather than `Object.hasOwn`: the latter is ES2022 and ships only
	// from Safari/iOS 15.4, while this app's browserslist floor is Safari/iOS 15 and the Vite target
	// (`es2021`/`safari15`) downlevels syntax without polyfilling built-ins. `getSortParam` runs from
	// `mounted()` on Contacts/Projects/Sessions, so a missing built-in would break those pages outright.
	const mapping = Object.prototype.hasOwnProperty.call(entityIndexSortMapping, entityType)
		? entityIndexSortMapping[entityType]
		: undefined;
	const sortParam = mapping && Object.prototype.hasOwnProperty.call(mapping, sortBy) ? mapping[sortBy] : undefined;

	if (!sortParam && fallback) {
		return "created";
	}

	return sortParam;
}

function shallowEqual(a: Record<string, any>, b: Record<string, any>): boolean {
	const aKeys = Object.keys(a);
	const bKeys = Object.keys(b);
	return aKeys.length == bKeys.length && aKeys.every((k) => a[k]?.toString() === b[k]?.toString());
}

export function mapResultToLightBoxArtifact(document: IndexableDocument): Partial<LightBoxArtifact> {
	const user = document.users?.[0];
	const parts = user?.name?.split(" ");
	const firstname = parts?.[0] ?? "";
	const lastname = parts && parts.length > 1 ? parts[1] : "";

	const artifact = {
		id: document.searchResultId ?? document.id,
		legacyId: document.id,
		type: document.entityType === "snapshot" ? "snapshot" : "recording",
		conversationId: document.conversationId,
		// cursor: plumbed because MediaLightBox's cross-organization guards read it and it was never mapped,
		// so every artifact-level org check in that component silently evaluated `undefined` and skipped —
		// guards that read as defence-in-depth while being unreachable. IndexableDocument carries it as a
		// required field on both the admin and API sides; only this mapper dropped it.
		organizationId: document.organizationId,
		customerId: document.customerId,
		mediaUrl: document.mediaUrl,
		thumbnailUrl: document.thumbnailUrl,
		parentId: document.parentId,
		notes: document.notes,
		createdAt: document.created,
		updatedAt: document.updated,
		status: document.status,
		User: {
			id: user?.id,
			firstname: firstname,
			lastname: lastname,
			name: user?.name,
			email: user?.email,
			phoneNumber: user?.phoneNumber
		},
		Labels: document.labels?.map((label) => ({
			id: label.id,
			name: label.name,
			baseColor: label.baseColor
		})),
		projectId: "",
		isUploadComplete: document.isUploadComplete,
		// Plumbed alongside isUploadComplete so the lightbox notice reads the same server verdict the
		// session banner does — the two must never disagree about one recording.
		isUploadAbandoned: document.isUploadAbandoned,
		// LSP-19254: the delete gate must know whether the two verdicts above mean anything —
		// IndexService stamps isUploadComplete = true for every non-async recording — and the mapper
		// was the only place the document's recordingType was being dropped.
		recordingType: document.recordingType,
		MasterRecording: document.masterRecordingId ? { id: document.masterRecordingId } : undefined,
		agentAvatarColor: document.agentAvatarColor,
		updating: document.updating
	} as Partial<LightBoxArtifact>;

	return artifact;
}

export const WEB_FORM_PAGE_SIZES = Object.freeze([10, 25, 50, 100]);

export function normalizePageSizeToSupportedOption(
	requestedPageSize: unknown,
	supportedPageSizes: readonly number[],
	defaultPageSize: number
): number {
	if (
		typeof requestedPageSize !== "number" ||
		!Number.isInteger(requestedPageSize) ||
		requestedPageSize <= 0 ||
		supportedPageSizes.length === 0
	) {
		return defaultPageSize;
	}

	const supportedSizesAtOrBelowRequest = supportedPageSizes.filter((supportedPageSize) => supportedPageSize <= requestedPageSize);
	return supportedSizesAtOrBelowRequest.length > 0 ? Math.max(...supportedSizesAtOrBelowRequest) : Math.min(...supportedPageSizes);
}

/**
 * LSP-18407: resolve a client-supplied `pageSize` query parameter to one of the page sizes the
 * page's own selector offers. Exact-match mode falls back to the page default; Web Forms opts into
 * normalizing canonical positive integers down to the nearest offered size, bounded by the list.
 *
 * A list page that seeds its page size from the URL used to honour whatever was typed there, so a
 * hand-edited `?pageSize=1000000` (or `0`, `-1`, `abc`) became the row limit sent to the API *and*
 * the divisor in the client-side page-count math.
 *
 * `unknown` input, not `string`: a repeated `?pageSize=10&pageSize=1000000` reaches a route query as
 * an array, and a caller may hand this a stored value of any shape. Anything that is not a string is
 * treated as absent.
 */
export function resolveQueryPageSize(
	rawPageSize: unknown,
	allowedPageSizes: readonly number[],
	defaultPageSize: number,
	normalizeToSupportedOption = false
): number {
	const requestedPageSize = typeof rawPageSize === "string" ? rawPageSize.trim() : undefined;
	// Matched as text against the offered sizes rather than parsed: parseInt would read "100abc" as
	// 100, and Number would accept "0x64"/"1e2"/"+100" as spellings of a listed size. Modulo
	// surrounding whitespace, only what the selector itself can put in the URL is honoured.
	// Deliberately not logged: this is client-controlled input evaluated on every list-page load, so a
	// stale bookmark or a crawler would emit unbounded warn volume into Datadog. contact-api made the
	// same call for the same parameter (fastifyPaging demoted its invalid-pageSize log to debug for
	// exactly this reason), and the discarded value is visible in the user's own URL.
	const exactPageSize = allowedPageSizes.find((pageSize) => String(pageSize) === requestedPageSize);
	if (exactPageSize !== undefined || !normalizeToSupportedOption) {
		return exactPageSize ?? defaultPageSize;
	}
	if (allowedPageSizes.length === 0 || requestedPageSize === undefined || !/^[1-9]\d*$/.test(requestedPageSize)) {
		return defaultPageSize;
	}

	const parsedPageSize = Number(requestedPageSize);
	const largestAllowedPageSize = Math.max(...allowedPageSizes);
	return parsedPageSize > largestAllowedPageSize
		? largestAllowedPageSize
		: normalizePageSizeToSupportedOption(parsedPageSize, allowedPageSizes, defaultPageSize);
}

/**
 * LSP-18407 (review follow-up): the `?page=` sibling of `resolveQueryPageSize`.
 *
 * `parseInt(getQueryParam("page") || "1")` let `?page=abc` seed `NaN`. The API coerces that away
 * (`args.page || 1`) so the server quietly serves page 1, but the client keeps NaN: it writes
 * `?page=NaN` back into the URL and every subsequent next/previous calculation stays NaN. A negative
 * page is worse — it reaches the API and 400s. Only a whole positive integer is honoured.
 */
export function resolveQueryPage(rawPage: unknown, defaultPage: number): number {
	return parseWholePositiveInteger(rawPage) ?? defaultPage;
}

/**
 * A whole positive integer, or `undefined` for anything else — the shared parse behind
 * `resolveQueryPage` and `parseSearchQuery`'s paging.
 *
 * Bounded at MAX_SAFE_INTEGER: `?page=99999999999999999999` parses to 1e20, which loses precision
 * before the API rejects it, so the docblock claim above ("whole positive integer") would not hold.
 */
function parseWholePositiveInteger(rawValue: unknown): number | undefined {
	const value = typeof rawValue === "string" ? rawValue.trim() : undefined;
	if (value === undefined || !/^\d+$/.test(value)) {
		return undefined;
	}
	const parsed = Number(value);
	return parsed >= 1 && Number.isSafeInteger(parsed) ? parsed : undefined;
}

// LSP-20656: the API refuses a global text search shorter than this (routes/search.ts
// validateGlobalSearch) because a 1-2 character term has no trigram and walks the whole
// organization. Gate it on the client too so the request is never sent and the user gets the reason
// instead of a generic "could not load" toast. Zero-width characters are stripped first so a pasted
// invisible character cannot make "ab" look long enough (same normalization the Sessions page uses).
export const MIN_GLOBAL_SEARCH_TERM_LENGTH = 3;
export const SHORT_GLOBAL_SEARCH_TERM_MESSAGE = `Type at least ${MIN_GLOBAL_SEARCH_TERM_LENGTH} characters to search.`;

export function isShortGlobalSearchTerm(searchTerm: string | null | undefined): boolean {
	const trimmedTerm = (searchTerm ?? "").trim();
	// Only a genuinely empty term is the browse-everything search. A term made solely of zero-width
	// characters is non-empty input the API will reject as too short, so it is reported as short here
	// rather than sent on to fail into the generic "could not load" toast (Codex review).
	if (trimmedTerm.length === 0) return false;
	const visibleLength = trimmedTerm.replace(/\u200B|\u200C|\u200D|\uFEFF/g, "").trim().length;
	return visibleLength < MIN_GLOBAL_SEARCH_TERM_LENGTH;
}
